import Keycloak from 'keycloak-js';

import { getKeycloakInitOptions, KEYCLOAK_CONFIG } from '@core/auth/config/keycloak.config';

import { environment } from '../../../environments/environment';

/** A Keycloak instance already initialized by the pre-bootstrap auth gate. */
export interface PreInitedKeycloak {
  keycloak: Keycloak;
  authenticated: boolean;
}

let _preInitedKeycloak: PreInitedKeycloak | null = null;

/**
 * Stash the instance initialized by {@link runPreBootstrapAuthGate} so that
 * `app.config.ts`'s `getKeycloakProviders()` can hand it straight to Angular DI
 * instead of constructing + initializing a second Keycloak instance.
 * Set by `main.ts` *before* it dynamically imports `app.config`.
 */
export function setPreInitedKeycloak(preInited: PreInitedKeycloak): void {
  _preInitedKeycloak = preInited;
}

/** The pre-bootstrap instance, or null on SSR / when the gate did not apply. */
export function getPreInitedKeycloak(): PreInitedKeycloak | null {
  return _preInitedKeycloak;
}

/**
 * Dev-only `?devToken=…` bypass, gated to localhost (mirrors
 * `getDevTokenFromUrl` in app.config.ts). When present we must NOT run the
 * interactive login-required gate — the app initializes Keycloak locally with
 * the injected token instead.
 */
function hasLocalhostDevToken(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return false;
  }

  return new URLSearchParams(window.location.search).has('devToken');
}

/**
 * HQDP-784 (solution 2): decide authentication BEFORE the Angular app bundle is
 * downloaded.
 *
 * When Keycloak is enabled and we are online (and not using the localhost
 * devToken bypass), create + initialize keycloak-js here, in `main.ts`, ahead of
 * `bootstrapApplication`. With `onLoad: 'login-required'`, an unauthenticated
 * user is redirected to the Keycloak login page WITHOUT ever downloading the
 * multi-MB app chunks — the redirect is no longer gated behind the full Angular
 * bootstrap (which previously meant ~20 s of JS download on a cold cache).
 *
 * Returns:
 *  - {@link PreInitedKeycloak} when online + enabled: the initialized instance,
 *    reused by the app (no second `init()`). If unauthenticated under
 *    login-required, the page has already navigated to Keycloak and this promise
 *    typically never resolves.
 *  - `null` when the gate does not apply (Keycloak disabled / offline / devToken)
 *    or when init fails — in all these cases the app boots and falls back to the
 *    legacy in-app `getKeycloakProviders()` path, unchanged.
 */
export async function runPreBootstrapAuthGate(): Promise<PreInitedKeycloak | null> {
  const online = typeof navigator === 'undefined' || navigator.onLine;
  if (!environment.keycloak.enabled || !online || hasLocalhostDevToken()) {
    return null;
  }

  const keycloak = new Keycloak({
    url: KEYCLOAK_CONFIG.url,
    realm: KEYCLOAK_CONFIG.realm,
    clientId: KEYCLOAK_CONFIG.clientId,
  });

  try {
    // login-required: redirects away when there is no session; only resolves
    // here (authenticated === true) when a session already exists / was created.
    const authenticated = await keycloak.init(getKeycloakInitOptions());

    return { keycloak, authenticated };
  } catch (error) {
    // Never hard-block boot on an auth transport failure: fall back to the
    // in-app Keycloak path, which retries init() via provideKeycloak().
    console.error('[keycloak-pre-bootstrap] init failed; falling back to in-app Keycloak init', error);

    return null;
  }
}
